import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ReactGoogleAutocomplete from "react-google-autocomplete";

import "./RegisterChat.css";
import STRINGS from "./chatLang";
import useAuthQuery from "../../store/auth/useAuthQuery";
import useCommonQuery from "../../store/common/useCommonQuery";
import useBusinessQuery from "../../store/Bussiness/useBusinessQuery";
import { getMyBussiness, getTrialplanDetails } from "../../store/Bussiness/bussinessUrl";
import { uploadFileToS3 } from "../../utils/s3Upload";
import {
  SOCIAL_FIELDS,
  emptyLimits,
  readPlanFeatures,
  capReached,
  stepAllowedFor,
} from "./planFeatures";
import {
  API_KEY,
  STATE_ID,
  MAX_IMAGE_MB,
  nowTime,
  onlyDigits,
  normalizeMobile,
  normalizeLink,
  looksLikeLink,
  compressImage,
  reverseGeocode,
} from "./chatHelpers";

/* Everything the visitor has typed is mirrored here, so a refresh or a
   dropped connection never loses their answers. Images are stored as
   uploaded S3 URLs — a picked File could not survive a reload. */
const STORAGE_KEY = "mc_register_chat_v1";

const RESEND_SECONDS = 30;

/* ── Step graph. Sub-flows (catalogue/cover/service/social) loop back here. ── */
const NEXT = {
  lang: "ownerName",
  ownerName: "mobile",
  mobile: "otp",
  otp: "bizName",
  bizName: "type",
  type: "category",
  category: "subCategory",
  subCategory: "location",
  location: "district",
  district: "postOffice",
  postOffice: "pincode",
  pincode: "about",
  about: "logo",
  logo: "banner",
  banner: "catalogueAsk",
  catalogueAsk: "coverAsk",
  cataloguePhoto: "catalogueTitle",
  catalogueTitle: "catalogueMore",
  catalogueMore: "coverAsk",
  coverAsk: "serviceAsk",
  coverPhoto: "coverMore",
  coverMore: "serviceAsk",
  serviceAsk: "socialAsk",
  serviceName: "serviceMore",
  serviceMore: "socialAsk",
  socialAsk: "review",
  socialWhatsapp: "socialFacebook",
  socialFacebook: "socialInstagram",
  socialInstagram: "socialYoutube",
  socialYoutube: "socialWebsite",
  socialWebsite: "socialGoogleReview",
  socialGoogleReview: "socialGoogleLocation",
  socialGoogleLocation: "review",
  review: "done",
};

/* Used only to draw the progress bar. */
const PROGRESS_ORDER = [
  "lang", "ownerName", "mobile", "otp", "bizName", "type", "category",
  "subCategory", "location", "district", "postOffice", "pincode", "about",
  "logo", "banner", "catalogueAsk", "coverAsk", "serviceAsk", "socialAsk",
  "review", "done",
];

/* A factory, not a constant — the nested arrays must never be shared. */
const emptyAnswers = () => ({
  ownerName: "",
  mobile: "",
  bizName: "",
  type: "shop",
  category: null,
  subCategory: null,
  location: null,
  district: null,
  postOffice: "",
  pincode: "",
  about: "",
  logo: "",
  banner: "",
  catalogue: [],
  cover: [],
  services: [],
  social: {},
});

/* Loop sub-steps don't appear in PROGRESS_ORDER; borrow the parent's slot
   so the bar doesn't jump while the visitor adds photos. */
const PROGRESS_ALIAS = {
  cataloguePhoto: "catalogueAsk",
  catalogueTitle: "catalogueAsk",
  catalogueMore: "catalogueAsk",
  coverPhoto: "coverAsk",
  coverMore: "coverAsk",
  serviceName: "serviceAsk",
  serviceMore: "serviceAsk",
  socialWhatsapp: "socialAsk",
  socialFacebook: "socialAsk",
  socialInstagram: "socialAsk",
  socialYoutube: "socialAsk",
  socialWebsite: "socialAsk",
  socialGoogleReview: "socialAsk",
  socialGoogleLocation: "socialAsk",
  /* Dead end after "you already have a business" — hold the bar where it was. */
  blocked: "otp",
};

/* Steps that sit inside a multi-part sequence — an edit from the review card
   must finish the sequence before coming back, not bail out after the first
   answer. The social chain ends at socialGoogleLocation, so that one is left
   out: answering it is what returns the visitor to the review. */
const LOOP_INTERNAL = new Set([
  "cataloguePhoto", "catalogueTitle", "coverPhoto", "serviceName",
  "socialWhatsapp", "socialFacebook", "socialInstagram", "socialYoutube",
  "socialWebsite", "socialGoogleReview",
]);

/* Skipping one of these leaves the whole loop rather than asking
   "add another?" about something the visitor just declined. */
const SKIP_TARGET = {
  serviceName: "serviceMore",
  cataloguePhoto: "catalogueMore",
  coverPhoto: "coverMore",
};

const RegisterChat = () => {
  const history = useHistory();

  const [lang, setLang] = useState("ml");
  /* A hand-edited or corrupt saved language must not blank the whole page. */
  const t = STRINGS[lang] || STRINGS.ml;

  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState("lang");
  const [answers, setAnswers] = useState(emptyAnswers);
  /* When the visitor taps Edit on the review card we re-ask one question
     and come straight back to the review instead of continuing the flow. */
  const [editing, setEditing] = useState(false);

  const [draft, setDraft] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [sheetSearch, setSheetSearch] = useState("");
  const [resendIn, setResendIn] = useState(0);
  /* Plan caps and feature flags; see planFeatures.js for the shape. */
  const [limits, setLimits] = useState(emptyLimits);
  const [createdId, setCreatedId] = useState(null);
  const [pendingPhoto, setPendingPhoto] = useState("");
  /* Where to drop the visitor back after a re-login mid-flow. */
  const [resumeStep, setResumeStep] = useState(null);

  const threadRef = useRef(null);
  const fileRef = useRef(null);
  const otpRefs = useRef([]);
  const restoredRef = useRef(false);

  /* Uploads take seconds. Async callbacks read these refs instead of the
     state they closed over, which by then may be several answers behind. */
  const stepRef = useRef(step);
  const answersRef = useRef(answers);
  const limitsRef = useRef(limits);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  useEffect(() => {
    limitsRef.current = limits;
  }, [limits]);

  const register = useAuthQuery.Registration();
  const sendOtp = useAuthQuery.SendOtp();
  const verifyOtp = useAuthQuery.VerifyOtp();
  const { mutateAsync: addBusiness } = useBusinessQuery.Add_business();

  const { data: categoryList, refetch: refetchCategories } =
    useCommonQuery.Get_category({ search: "", limit: 2000, offset: 0 });
  const { data: subCategoryList, refetch: refetchSubCategories } =
    useCommonQuery.Get_subcategory({
      id: answers.category?.id,
      search: "",
      limit: 2000,
      offset: 0,
    });
  const { data: districtList, refetch: refetchDistricts } =
    useCommonQuery.Get_district(STATE_ID);

  /* ── Message helpers ── */

  const pushMsg = useCallback((msg) => {
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, ts: nowTime(), ...msg },
    ]);
  }, []);

  const pushBot = useCallback(
    (text, delay = 420) => {
      if (!text) return;
      setTyping(true);
      window.setTimeout(() => {
        setTyping(false);
        pushMsg({ from: "bot", text });
      }, delay);
    },
    [pushMsg]
  );

  const pushMe = useCallback((text) => pushMsg({ from: "me", text }), [pushMsg]);

  const pushError = useCallback(
    (text) => pushMsg({ from: "bot", text, error: true }),
    [pushMsg]
  );

  /* ── Question text for a step ── */

  const questionFor = useCallback(
    (id) => {
      switch (id) {
        case "ownerName": return t.askOwnerName;
        case "mobile": return t.askMobile;
        case "bizName": return t.askBizName;
        case "type": return t.askType;
        case "category": return t.askCategory;
        case "subCategory": return t.askSubCategory;
        case "location": return t.askLocation;
        case "district": return t.askDistrict;
        case "postOffice": return t.askPostOffice;
        case "pincode": return t.askPincode;
        case "about": return t.askAbout;
        case "logo": return t.askLogo;
        case "banner": return t.askBanner;
        case "catalogueAsk": return t.askCatalogue;
        case "cataloguePhoto": return t.sendPhoto;
        case "catalogueTitle": return t.askCatalogueTitle;
        case "catalogueMore": return t.askCatalogueMore;
        case "coverAsk": return t.askCover;
        case "coverPhoto": return t.sendPhoto;
        case "coverMore": return t.askCoverMore;
        case "serviceAsk": return t.askServices;
        case "serviceName": return t.askServices;
        case "serviceMore": return t.askServicesMore;
        case "socialAsk": return t.askSocial;
        case "socialWhatsapp": return t.askWhatsapp;
        case "socialFacebook": return t.askFacebook;
        case "socialInstagram": return t.askInstagram;
        case "socialYoutube": return t.askYoutube;
        case "socialWebsite": return t.askWebsite;
        case "socialGoogleReview": return t.askGoogleReview;
        case "socialGoogleLocation": return t.askGoogleLocation;
        case "review": return t.reviewIntro;
        default: return "";
      }
    },
    [t]
  );

  /* ── Plan gating ── */

  /* Read through the ref, not the state: the async upload and submit paths
     below run long after the render that closed over `limits`. */
  const stepAllowed = useCallback((id) => stepAllowedFor(limitsRef.current, id), []);

  /* Walk forward past every step this plan hides, so the chat never lands on
     a question it is only going to skip. */
  const resolveStep = useCallback(
    (id) => {
      let target = id;
      /* NEXT is a graph rather than a list; `seen` stops a malformed cycle
         from looping forever. */
      const seen = new Set();
      while (target && !stepAllowed(target) && !seen.has(target)) {
        seen.add(target);
        target = NEXT[target] || "review";
      }
      return target;
    },
    [stepAllowed]
  );

  /* Move to a step and ask its question. */
  const goTo = useCallback(
    (rawId, delay = 420) => {
      /* Applied here rather than in advance(), so direct jumps — an edit from
         the review card, the first social step, a resumed session — are gated
         by the plan too. */
      const id = resolveStep(rawId);
      setStep(id);
      setDraft("");
      setSheetSearch("");
      if (id === "review") {
        setTyping(true);
        window.setTimeout(() => {
          setTyping(false);
          /* Only ever one review card in the thread — otherwise every edit
             leaves another stale copy with its own live Edit buttons. */
          setMessages((prev) => [
            ...prev.filter((m) => !m.review && !m.reviewIntro),
            {
              id: `rev_intro_${Date.now()}`,
              ts: nowTime(),
              from: "bot",
              text: t.reviewIntro,
              reviewIntro: true,
            },
            { id: `rev_${Date.now()}`, ts: nowTime(), from: "bot", review: true },
          ]);
        }, delay);
        return;
      }
      if (id === "done") return;
      pushBot(questionFor(id), delay);
    },
    [pushBot, questionFor, resolveStep, t]
  );

  /* After an answer: back to review if editing, otherwise follow the graph.
     Loop-internal steps keep following the graph so an edited catalogue can
     take several photos before returning. */
  const advance = useCallback(
    (from) => {
      if (editing && !LOOP_INTERNAL.has(from)) {
        setEditing(false);
        goTo("review");
        return;
      }
      goTo(NEXT[from] || "review");
    },
    [editing, goTo]
  );

  /* ── Persistence ── */

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        const savedLang = STRINGS[saved.lang] ? saved.lang : "ml";
        const hasToken = Boolean(localStorage.getItem("accessToken"));
        const isKnownStep =
          Boolean(NEXT[saved.step]) ||
          saved.step === "done" ||
          saved.step === "blocked";
        let savedStep = isKnownStep ? saved.step : "lang";
        /* The photo a catalogue title belongs to is uploaded before the title
           is asked. If that URL didn't survive, ask for the photo again rather
           than saving a catalogue row with an empty image. */
        if (savedStep === "catalogueTitle" && !saved.pendingPhoto) {
          savedStep = "cataloguePhoto";
        }
        const needsAuth =
          savedStep !== "lang" &&
          savedStep !== "ownerName" &&
          savedStep !== "mobile" &&
          savedStep !== "otp";

        /* A stored null where an array belongs would crash the review card. */
        const base = emptyAnswers();
        const restored = { ...base, ...(saved.answers || {}) };
        ["catalogue", "cover", "services"].forEach((k) => {
          if (!Array.isArray(restored[k])) restored[k] = [];
        });
        if (!restored.social || typeof restored.social !== "object") restored.social = {};

        setLang(savedLang);
        setAnswers(restored);
        setMessages(saved.messages || []);
        setPendingPhoto(saved.pendingPhoto || "");

        /* If the token is gone, everything after verification is unusable.
           Rewind to the number question but remember where to come back to,
           so the visitor isn't re-asked questions they already answered
           (which would also double up catalogue/cover/service entries). */
        if (needsAuth && !hasToken) {
          setStep("mobile");
          setResumeStep(saved.resumeStep || savedStep);
          const s = STRINGS[savedLang];
          setMessages([
            ...(saved.messages || []),
            {
              id: `resume_${Date.now()}`,
              ts: nowTime(),
              from: "bot",
              text: `${s.sessionExpired}\n\n${s.askMobile}`,
            },
          ]);
        } else {
          setStep(savedStep);
          /* Mid re-login: keep the target until OTP succeeds. */
          if (savedStep === "mobile" || savedStep === "otp") {
            setResumeStep(saved.resumeStep || null);
          }
        }
        setEditing(Boolean(saved.editing));
        setCreatedId(saved.createdId || null);
        restoredRef.current = true;
      }
    } catch (e) {
      /* A corrupt draft should never block registration — start fresh. */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          lang,
          step,
          answers,
          /* An in-flight upload holds a blob: preview that dies with the page,
             so those bubbles are left out of the snapshot. */
          messages: messages.filter((m) => !m.uploading),
          editing,
          createdId,
          pendingPhoto,
          /* Persisted too: the rewind overwrites `step` with "mobile", so
             without this a second reload would forget where they were. */
          resumeStep,
        })
      );
    } catch (e) {
      /* Storage full or blocked (private mode) — the chat still works. */
    }
  }, [lang, step, answers, messages, editing, createdId, pendingPhoto, resumeStep]);

  /* Opening greeting, first visit only. */
  useEffect(() => {
    if (restoredRef.current || messages.length) return;
    pushMsg({ from: "bot", text: STRINGS.ml.greet });
    window.setTimeout(
      () =>
        pushMsg({
          from: "bot",
          text: "മലയാളം അല്ലെങ്കിൽ English — ഏതു വേണം?\nMalayalam or English?",
        }),
      500
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Caps and feature flags are normally fetched right after verification, but
     a reload never passes through that path. Without this a restored chat
     would go back to asking questions the plan hides. */
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) return;
    loadPlanLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing, step]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  /* ── Auth ── */

  const requestOtp = useCallback(
    async (mobile, name) => {
      /* There is no "does this number exist" endpoint, so try to register
         and fall back to login when the backend says it already exists. */
      try {
        await register.mutateAsync({ name: name || "user", mobile });
        return true;
      } catch (err) {
        /* The error shape varies (message / field errors / non_field_errors),
           so match against the whole body. */
        const body = err?.response?.data ?? err?.data ?? "";
        const msg = typeof body === "string" ? body : JSON.stringify(body);
        if (/exist/i.test(msg)) {
          await sendOtp.mutateAsync({ phoneNumber: mobile });
          return true;
        }
        throw err;
      }
    },
    [register, sendOtp]
  );

  const handleMobileSubmit = async (raw) => {
    const mobile = normalizeMobile(raw);
    if (mobile.length !== 10) {
      pushError(t.errMobile);
      return;
    }
    pushMe(mobile);
    setAnswers((a) => ({ ...a, mobile }));
    setDraft("");
    setBusy(true);
    pushBot(t.sendingOtp, 200);
    try {
      await requestOtp(mobile, answers.ownerName);
      setResendIn(RESEND_SECONDS);
      setStep("otp");
      pushBot(t.otpSent(mobile), 500);
    } catch (err) {
      pushError(
        err?.response?.data?.message || err?.data?.message || t.errNetwork
      );
    } finally {
      setBusy(false);
    }
  };

  const loadPlanLimits = async () => {
    try {
      const res = await getTrialplanDetails();
      const next = readPlanFeatures(
        res?.data?.results?.[0]?.subscription_features
      );
      /* A plan with no feature row configured tells us nothing — leaving the
         limits untouched keeps every question in the flow. Without this an
         empty object would read as "every feature off" and gut the chat. */
      if (!next) return;
      /* The ref is written here as well as by its effect: the very next
         render's review card and progress bar read the flags through the ref,
         and an effect would not have run by then. */
      limitsRef.current = next;
      setLimits(next);
    } catch (e) {
      /* Without the plan we ask everything and enforce no cap. */
    }
  };

  const handleOtpSubmit = async () => {
    const code = otpDigits.join("");
    if (code.length !== 4) {
      pushError(t.errOtp);
      return;
    }
    setBusy(true);
    try {
      await verifyOtp.mutateAsync({ mobile: answers.mobile, otp: code });
      pushMe("••••");
      setOtpDigits(["", "", "", ""]);
      pushBot(t.verified, 220);

      /* One owner may hold only one business, so stop here instead of
         asking fifteen questions and failing on submit. */
      const mine = await getMyBussiness().catch(() => null);
      if (mine?.data?.results?.length) {
        setStep("blocked");
        pushBot(t.alreadyHasBusiness, 600);
        return;
      }
      await loadPlanLimits();
      /* Coming back from a lost session? Pick up where they stopped. */
      const usable =
        resumeStep && !["lang", "ownerName", "mobile", "otp", "done"].includes(resumeStep);
      const target = usable ? resumeStep : "bizName";
      setResumeStep(null);
      window.setTimeout(() => goTo(target), 600);
    } catch (err) {
      pushError(
        err?.response?.data?.message || err?.data?.message || t.otpWrong
      );
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || busy) return;
    setBusy(true);
    try {
      await sendOtp.mutateAsync({ phoneNumber: answers.mobile });
      setResendIn(RESEND_SECONDS);
      pushBot(t.otpResent, 200);
    } catch (err) {
      pushError(t.errNetwork);
    } finally {
      setBusy(false);
    }
  };

  /* ── Text answers ── */

  const submitText = async () => {
    const value = draft.trim();

    if (step === "mobile") return handleMobileSubmit(draft);

    const required = { ownerName: t.errName, bizName: t.errBizName, catalogueTitle: t.errRequired };
    if (required[step] && !value) {
      pushError(required[step]);
      return;
    }
    if (step === "about" && value.length > 1000) {
      pushError(t.errTooLong);
      return;
    }
    if (step === "pincode" && value && onlyDigits(value).length !== 6) {
      pushError(t.errPincode);
      return;
    }

    switch (step) {
      case "ownerName":
        pushMe(value);
        setAnswers((a) => ({ ...a, ownerName: value }));
        advance("ownerName");
        break;
      case "bizName":
        pushMe(value);
        setAnswers((a) => ({ ...a, bizName: value }));
        advance("bizName");
        break;
      case "postOffice":
        pushMe(value);
        setAnswers((a) => ({ ...a, postOffice: value }));
        advance("postOffice");
        break;
      case "pincode":
        pushMe(onlyDigits(value));
        setAnswers((a) => ({ ...a, pincode: onlyDigits(value) }));
        advance("pincode");
        break;
      case "about":
        pushMe(value);
        setAnswers((a) => ({ ...a, about: value }));
        advance("about");
        break;
      case "catalogueTitle": {
        pushMe(value);
        const item = { url: pendingPhoto, name: value };
        setAnswers((a) => ({ ...a, catalogue: [...a.catalogue, item] }));
        setPendingPhoto("");
        const count = answers.catalogue.length + 1;
        if (capReached(limits.catalogue, count)) {
          pushBot(t.catalogueLimit(limits.catalogue), 320);
          advance("catalogueMore");
        } else {
          advance("catalogueTitle");
        }
        break;
      }
      case "serviceName": {
        if (!value) {
          advance("serviceMore");
          break;
        }
        pushMe(value);
        setAnswers((a) => ({ ...a, services: [...a.services, value] }));
        const count = answers.services.length + 1;
        if (capReached(limits.service, count)) {
          pushBot(t.serviceLimit(limits.service), 320);
          advance("serviceMore");
        } else {
          advance("serviceName");
        }
        break;
      }
      default: {
        /* Social link steps */
        const field = SOCIAL_FIELDS[step];
        if (!field) return;
        if (!value) {
          advance(step);
          break;
        }
        if (step === "socialWhatsapp") {
          const num = normalizeMobile(value);
          if (num.length < 10) {
            pushError(t.errMobile);
            return;
          }
          pushMe(num);
          setAnswers((a) => ({ ...a, social: { ...a.social, [field]: num } }));
          advance(step);
          break;
        }
        if (!looksLikeLink(value)) {
          pushError(t.errLink);
          return;
        }
        const link = normalizeLink(value);
        pushMe(link);
        setAnswers((a) => ({ ...a, social: { ...a.social, [field]: link } }));
        advance(step);
      }
    }
  };

  /* ── Choice answers ── */

  const chooseLang = (code) => {
    setLang(code);
    pushMe(code === "ml" ? "മലയാളം" : "English");
    const s = STRINGS[code];
    setTyping(true);
    window.setTimeout(() => {
      setTyping(false);
      pushMsg({ from: "bot", text: s.greet2 });
      setStep("ownerName");
      window.setTimeout(() => pushMsg({ from: "bot", text: s.askOwnerName }), 500);
    }, 400);
  };

  const chooseType = (value) => {
    pushMe(value === "shop" ? t.typeShop : t.typeService);
    setAnswers((a) => ({ ...a, type: value }));
    advance("type");
  };

  const choosePick = (kind, item) => {
    pushMe(item.name);
    if (kind === "category") {
      /* A new category invalidates whatever sub category was chosen, so the
         sub category must be asked again — even when editing from review. */
      setAnswers((a) => ({
        ...a,
        category: { id: item.id, name: item.name },
        subCategory: null,
      }));
      if (editing) goTo("subCategory");
      else advance("category");
    } else if (kind === "subCategory") {
      setAnswers((a) => ({ ...a, subCategory: { id: item.id, name: item.name } }));
      advance("subCategory");
    } else {
      setAnswers((a) => ({ ...a, district: { id: item.id, name: item.name } }));
      advance("district");
    }
  };

  const answerYesNo = (yes) => {
    pushMe(yes ? t.yes : t.no);
    switch (step) {
      case "catalogueAsk":
      case "catalogueMore":
        if (yes) {
          if (capReached(limits.catalogue, answers.catalogue.length)) {
            pushBot(t.catalogueLimit(limits.catalogue), 300);
            advance("catalogueMore");
          } else {
            goTo("cataloguePhoto");
          }
        } else advance(step === "catalogueAsk" ? "catalogueAsk" : "catalogueMore");
        break;
      case "coverAsk":
      case "coverMore":
        if (yes) {
          if (capReached(limits.cover, answers.cover.length)) {
            pushBot(t.coverLimit(limits.cover), 300);
            advance("coverMore");
          } else {
            goTo("coverPhoto");
          }
        } else advance(step === "coverAsk" ? "coverAsk" : "coverMore");
        break;
      case "serviceAsk":
      case "serviceMore":
        if (yes) {
          if (capReached(limits.service, answers.services.length)) {
            pushBot(t.serviceLimit(limits.service), 300);
            advance("serviceMore");
          } else {
            goTo("serviceName");
          }
        } else advance(step === "serviceAsk" ? "serviceAsk" : "serviceMore");
        break;
      case "socialAsk":
        if (yes) goTo("socialWhatsapp");
        else advance("socialAsk");
        break;
      default:
        break;
    }
  };

  const skipStep = () => {
    pushMe(t.skip);
    /* Skipping means "I don't want this", so an existing value is cleared —
       otherwise Edit → Skip would silently keep the old answer. */
    if (step === "subCategory") setAnswers((a) => ({ ...a, subCategory: null }));
    if (step === "logo") setAnswers((a) => ({ ...a, logo: "" }));
    if (step === "banner") setAnswers((a) => ({ ...a, banner: "" }));
    if (step === "pincode") setAnswers((a) => ({ ...a, pincode: "" }));
    if (step === "postOffice") setAnswers((a) => ({ ...a, postOffice: "" }));
    if (step === "about") setAnswers((a) => ({ ...a, about: "" }));
    advance(SKIP_TARGET[step] || step);
  };

  /* ── Location ── */

  const applyLocation = async (lat, lng, placeName) => {
    const geo = await reverseGeocode(lat, lng);
    const label = placeName || geo.locality || `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
    const needle = (geo.districtName || "").toLowerCase().split(" ")[0];
    const guess =
      (needle &&
        districtList?.data?.results?.find((d) =>
          (d?.name || "").toLowerCase().includes(needle)
        )) ||
      null;

    setAnswers((a) => ({
      ...a,
      location: { lat, lng, name: label },
      postOffice: a.postOffice || geo.postOffice || "",
      pincode: a.pincode || onlyDigits(geo.pincode || ""),
      district: a.district || (guess ? { id: guess.id, name: guess.name } : null),
    }));

    pushMsg({ from: "me", text: `📍 ${label}` });
    pushBot(t.locationSet(label), 320);

    if (editing) {
      setEditing(false);
      window.setTimeout(() => goTo("review"), 700);
      return;
    }
    /* The district may already be filled in from the geocode; still confirm it. */
    window.setTimeout(() => goTo("district"), 700);
  };

  const useGps = () => {
    if (!navigator.geolocation) {
      pushError(t.locationDenied);
      return;
    }
    setBusy(true);
    pushBot(t.locating, 150);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        /* Without the finally, a throw in here would leave the composer
           disabled with no way out but a reload. */
        try {
          await applyLocation(pos.coords.latitude, pos.coords.longitude, "");
        } catch (err) {
          pushError(t.locationDenied);
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        pushError(t.locationDenied);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const onPlacePicked = async (place) => {
    if (!place?.geometry?.location) return;
    setBusy(true);
    try {
      await applyLocation(
        place.geometry.location.lat(),
        place.geometry.location.lng(),
        place.name || place.formatted_address || ""
      );
    } catch (err) {
      pushError(t.locationDenied);
    } finally {
      setBusy(false);
    }
  };

  /* ── Images ── */

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMAGE_MB * 1024 * 1024 * 3) {
      /* Very large originals are still accepted — compression brings them
         down — but anything absurd is rejected up front. */
      pushError(t.errImage);
      return;
    }

    /* The step at pick time. An upload takes seconds, and the visitor can
       move on meanwhile, so the result is only applied if we're still here. */
    const atStep = step;
    const localId = `up_${Date.now()}`;
    const preview = URL.createObjectURL(file);
    pushMsg({ from: "me", image: preview, uploading: true, localId });
    setBusy(true);

    try {
      const compressed = await compressImage(file);
      if (compressed.size > MAX_IMAGE_MB * 1024 * 1024) {
        throw new Error(t.errImage);
      }
      const url = await uploadFileToS3(compressed);
      setMessages((prev) =>
        prev.map((m) => (m.localId === localId ? { ...m, image: url, uploading: false } : m))
      );
      URL.revokeObjectURL(preview);

      if (stepRef.current !== atStep) return;

      switch (atStep) {
        case "logo":
          setAnswers((a) => ({ ...a, logo: url }));
          advance("logo");
          break;
        case "banner":
          setAnswers((a) => ({ ...a, banner: url }));
          advance("banner");
          break;
        case "cataloguePhoto":
          setPendingPhoto(url);
          goTo("catalogueTitle");
          break;
        case "coverPhoto": {
          const count = (answersRef.current.cover?.length || 0) + 1;
          setAnswers((a) => ({ ...a, cover: [...a.cover, url] }));
          const cap = limitsRef.current.cover;
          if (capReached(cap, count)) {
            pushBot(t.coverLimit(cap), 320);
            advance("coverMore");
          } else {
            goTo("coverMore");
          }
          break;
        }
        default:
          break;
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.localId !== localId));
      URL.revokeObjectURL(preview);
      /* s3Upload throws raw English internals; keep the chat in one language. */
      pushError(t.errNetwork);
    } finally {
      setBusy(false);
    }
  };

  /* ── Review + submit ── */

  const editFromReview = (target) => {
    setEditing(true);
    goTo(target, 250);
  };

  const buildPayload = () => {
    const payload = {
      name: answers.bizName,
      type: answers.type || "shop",
      mobile_number: answers.mobile,
      category: answers.category?.id,
      sub_category: answers.subCategory?.id,
      district: answers.district?.id,
      post_office: answers.postOffice,
      pin_code: answers.pincode,
      latitude: answers.location?.lat,
      longitude: answers.location?.lng,
      about_us: answers.about,
      image: answers.logo,
      banner_image: answers.banner,
      location_info: answers.location?.name || "",
      catalogue_image: answers.catalogue.map((item) => ({
        catalogue_image: item.url,
        is_active: true,
        name: item.name,
      })),
      cover_image: answers.cover.map((url) => ({
        cover_image: url,
        is_active: true,
      })),
      social_medias: {
        google_location_link: answers.social.google_location_link || "",
        whatsapp_number: answers.social.whatsapp_number || "",
        facebook: answers.social.facebook || "",
        instagram: answers.social.instagram || "",
        google_review_link: answers.social.google_review_link || "",
        youtube: answers.social.youtube || "",
        website: answers.social.website || "",
      },
      service: answers.services.map((name) => ({ name, is_active: true })),
    };
    if (!payload.pin_code) delete payload.pin_code;
    if (!payload.sub_category) delete payload.sub_category;
    if (!payload.district) delete payload.district;

    /* A saved draft can outlive a plan change — someone starts the chat while
       catalogue photos are allowed, the admin sets the count to 0, then they
       come back and submit. business/create/ rejects the whole request when a
       block exceeds the plan, so anything this plan no longer allows is
       dropped here rather than failing the submit. */
    if (!stepAllowed("catalogueAsk")) payload.catalogue_image = [];
    if (!stepAllowed("coverAsk")) payload.cover_image = [];
    if (!stepAllowed("serviceAsk")) payload.service = [];
    if (!stepAllowed("about")) payload.about_us = "";
    if (!stepAllowed("banner")) payload.banner_image = "";
    if (!stepAllowed("subCategory")) delete payload.sub_category;
    /* Same for the links: a field the plan hides is not sent at all. */
    Object.entries(SOCIAL_FIELDS).forEach(([stepId, field]) => {
      if (!stepAllowed(stepId)) payload.social_medias[field] = "";
    });
    return payload;
  };

  const handleSubmit = async () => {
    if (busy || step === "done") return;
    setBusy(true);
    try {
      const res = await addBusiness(buildPayload());
      /* The shared axios interceptor swallows 401/403/404 and network errors
         by resolving undefined instead of rejecting. Without this guard the
         chat would congratulate the visitor on a business that was never
         created. */
      if (!res) throw new Error("no-response");
      const id = res?.data?.id || res?.id || null;
      setCreatedId(id);
      setStep("done");
      pushBot(t.doneTitle, 300);
      window.setTimeout(() => pushMsg({ from: "bot", text: t.doneBody }), 900);
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.response?.data?.message ||
        err?.data?.detail ||
        "";
      pushError(t.submitFailed(msg || t.errNetwork));
    } finally {
      setBusy(false);
    }
  };

  const finishAndLeave = (path) => {
    localStorage.removeItem(STORAGE_KEY);
    history.push(path);
  };

  /* ── Derived ── */

  const progress = useMemo(() => {
    /* Skipped questions are dropped from the denominator too, otherwise the
       bar would stop short of 100% on a plan that asks fewer of them.
       "review" and "done" are the two fixed ends and are never gated. */
    const order = PROGRESS_ORDER.filter(
      (id) => id === "review" || id === "done" || stepAllowed(id)
    );
    const i = order.indexOf(PROGRESS_ALIAS[step] || step);
    if (i < 0) return 100;
    return Math.round((i / (order.length - 1)) * 100);
    /* `limits` is not read directly — it is here so the bar recomputes once
       the plan arrives and stepAllowed starts returning false for some steps. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, stepAllowed, limits]);

  /* The review card stays in the thread after submitting, so its Edit buttons
     have to stop working — otherwise scrolling up and editing lets the visitor
     submit a second business. */
  const reviewLocked = step === "done" || step === "blocked";

  const sheetKind =
    step === "category" ? "category" : step === "subCategory" ? "subCategory" : step === "district" ? "district" : null;

  const sheetItems = useMemo(() => {
    let list = [];
    if (sheetKind === "category") list = categoryList?.data?.results || [];
    if (sheetKind === "subCategory") list = subCategoryList?.data?.results || [];
    if (sheetKind === "district") list = districtList?.data?.results || [];
    const q = sheetSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((i) => (i.name || "").toLowerCase().includes(q));
  }, [sheetKind, sheetSearch, categoryList, subCategoryList, districtList]);

  const reviewRows = useMemo(
    () => [
      { key: "ownerName", label: t.labels.ownerName, value: answers.ownerName, editable: false },
      { key: "mobile", label: t.labels.mobile, value: answers.mobile, editable: false },
      { key: "bizName", label: t.labels.bizName, value: answers.bizName },
      { key: "type", label: t.labels.type, value: answers.type === "shop" ? t.typeShop : t.typeService },
      { key: "category", label: t.labels.category, value: answers.category?.name },
      { key: "subCategory", label: t.labels.subCategory, value: answers.subCategory?.name },
      { key: "location", label: t.labels.location, value: answers.location?.name },
      { key: "district", label: t.labels.district, value: answers.district?.name },
      { key: "postOffice", label: t.labels.postOffice, value: answers.postOffice },
      { key: "pincode", label: t.labels.pincode, value: answers.pincode },
      { key: "about", label: t.labels.about, value: answers.about },
      { key: "logo", label: t.labels.logo, images: answers.logo ? [answers.logo] : [] },
      { key: "banner", label: t.labels.banner, images: answers.banner ? [answers.banner] : [] },
      {
        key: "catalogueAsk",
        label: t.labels.catalogue,
        images: answers.catalogue.map((c) => c.url),
      },
      { key: "coverAsk", label: t.labels.cover, images: answers.cover },
      { key: "serviceAsk", label: t.labels.services, value: answers.services.join(", ") },
      {
        key: "socialAsk",
        label: t.labels.social,
        value: Object.values(answers.social).filter(Boolean).join("\n"),
      },
    /* Row keys are step ids, so the same gate that skipped the question hides
       its row — a plan that never asked about Instagram shouldn't show an
       empty Instagram line for the visitor to "edit". */
    ].filter((row) => stepAllowed(row.key)),
    /* `limits` again only to trigger a recompute when the plan lands. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [answers, t, stepAllowed, limits]
  );

  /* ── Composer ── */

  const OPTIONAL_TEXT = [
    "postOffice", "pincode", "about", "serviceName", ...Object.keys(SOCIAL_FIELDS),
  ];
  const IMAGE_STEPS = ["logo", "banner", "cataloguePhoto", "coverPhoto"];
  /* Every image step can be skipped — including the loop photos, so someone
     who changes their mind (or whose upload keeps failing) isn't trapped. */
  const OPTIONAL_IMAGE = ["logo", "banner", "cataloguePhoto", "coverPhoto"];
  const YESNO_STEPS = [
    "catalogueAsk", "catalogueMore", "coverAsk", "coverMore",
    "serviceAsk", "serviceMore", "socialAsk",
  ];

  const renderComposer = () => {
    if (step === "lang") {
      return (
        <div className="rc-chips">
          <button className="rc-chip primary" onClick={() => chooseLang("ml")}>
            മലയാളം
          </button>
          <button className="rc-chip" onClick={() => chooseLang("en")}>
            English
          </button>
        </div>
      );
    }

    if (step === "blocked") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={() => finishAndLeave("/my-list")}>
            {t.viewMyListing}
          </button>
        </div>
      );
    }

    if (step === "otp") {
      return (
        <>
          <div className="rc-otp-row">
            {otpDigits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (otpRefs.current[i] = el)}
                className="rc-otp-box"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => {
                  const v = onlyDigits(e.target.value).slice(0, 1);
                  const next = [...otpDigits];
                  next[i] = v;
                  setOtpDigits(next);
                  if (v && i < 3) otpRefs.current[i + 1]?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !otpDigits[i] && i > 0) {
                    otpRefs.current[i - 1]?.focus();
                  }
                }}
              />
            ))}
          </div>
          <div className="rc-inline-links">
            <button
              className="rc-link-btn"
              disabled={resendIn > 0 || busy}
              onClick={handleResend}
            >
              {resendIn > 0 ? `${t.resend} (${resendIn}s)` : t.resend}
            </button>
            <button
              className="rc-link-btn"
              onClick={() => {
                setOtpDigits(["", "", "", ""]);
                goTo("mobile", 200);
              }}
            >
              {t.changeNumber}
            </button>
          </div>
          <div className="rc-input-row">
            <button
              className="rc-send"
              disabled={busy || otpDigits.join("").length !== 4}
              onClick={handleOtpSubmit}
            >
              <SendIcon fontSize="small" />
            </button>
          </div>
        </>
      );
    }

    if (step === "type") {
      return (
        <div className="rc-chips">
          <button className="rc-chip primary" onClick={() => chooseType("shop")}>
            🏪 {t.typeShop}
          </button>
          <button className="rc-chip" onClick={() => chooseType("service")}>
            🛠 {t.typeService}
          </button>
        </div>
      );
    }

    if (sheetKind) {
      return (
        <>
          <div className="rc-sheet">
            <div className="rc-sheet-search">
              <input
                value={sheetSearch}
                onChange={(e) => setSheetSearch(e.target.value)}
                placeholder={t.search}
              />
            </div>
            <div className="rc-sheet-list">
              {sheetItems.length === 0 && (
                <div className="rc-sheet-empty">{t.noResults}</div>
              )}
              {sheetItems.map((item) => (
                <div
                  key={item.id}
                  className="rc-sheet-item"
                  onClick={() => choosePick(sheetKind, item)}
                >
                  {item.image ? <img src={item.image} alt="" /> : null}
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rc-chips">
            {step === "subCategory" && (
              <button className="rc-chip ghost" onClick={skipStep}>
                {t.skip}
              </button>
            )}
            {/* Category and district are required, so a failed fetch would be
                a dead end without a way to ask for the list again. */}
            {sheetItems.length === 0 && !sheetSearch.trim() && (
              <button
                className="rc-chip"
                onClick={() => {
                  if (sheetKind === "category") refetchCategories();
                  else if (sheetKind === "subCategory") refetchSubCategories();
                  else refetchDistricts();
                }}
              >
                {t.retry}
              </button>
            )}
          </div>
        </>
      );
    }

    if (step === "location") {
      return (
        <>
          <div className="rc-location-search">
            <ReactGoogleAutocomplete
              apiKey={API_KEY}
              options={{
                types: ["neighborhood", "locality", "sublocality", "postal_code", "point_of_interest"],
                componentRestrictions: { country: "in" },
                fields: ["address_components", "geometry", "name", "place_id", "formatted_address"],
              }}
              onPlaceSelected={onPlacePicked}
              placeholder={t.searchPlace}
            />
          </div>
          <div className="rc-chips">
            <button className="rc-chip primary block" disabled={busy} onClick={useGps}>
              <MyLocationIcon style={{ fontSize: 16, verticalAlign: "-3px", marginRight: 6 }} />
              {t.shareLocation}
            </button>
          </div>
        </>
      );
    }

    if (YESNO_STEPS.includes(step)) {
      return (
        <div className="rc-chips">
          <button className="rc-chip primary" onClick={() => answerYesNo(true)}>
            {t.yes}
          </button>
          <button className="rc-chip ghost" onClick={() => answerYesNo(false)}>
            {t.no}
          </button>
        </div>
      );
    }

    if (IMAGE_STEPS.includes(step)) {
      return (
        <>
          <div className="rc-chips">
            <button
              className="rc-chip primary"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              <PhotoCameraIcon style={{ fontSize: 16, verticalAlign: "-3px", marginRight: 6 }} />
              {busy ? t.uploading : t.addPhoto}
            </button>
            {OPTIONAL_IMAGE.includes(step) && (
              <button className="rc-chip ghost" disabled={busy} onClick={skipStep}>
                {t.skip}
              </button>
            )}
          </div>
        </>
      );
    }

    if (step === "review") {
      return (
        <div className="rc-cta">
          <button className="solid" disabled={busy} onClick={handleSubmit}>
            {busy ? t.submitting : t.submit}
          </button>
        </div>
      );
    }

    if (step === "done") {
      return (
        <div className="rc-cta">
          {createdId && (
            <button
              className="solid"
              onClick={() => finishAndLeave(`/product-details/${createdId}`)}
            >
              {t.previewBtn}
            </button>
          )}
          <button className="outline" onClick={() => finishAndLeave("/my-list")}>
            {t.myListingsBtn}
          </button>
        </div>
      );
    }

    /* Default: a text / number field */
    const numeric = step === "mobile" || step === "pincode" || step === "socialWhatsapp";
    return (
      <>
        {OPTIONAL_TEXT.includes(step) && (
          <div className="rc-chips">
            <button className="rc-chip ghost" onClick={skipStep}>
              {t.skip}
            </button>
          </div>
        )}
        <div className="rc-input-row">
          <div className="rc-input-shell">
            <textarea
              className="rc-input"
              rows={1}
              value={draft}
              inputMode={numeric ? "numeric" : "text"}
              placeholder={t.typeHere}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!busy) submitText();
                }
              }}
            />
          </div>
          <button
            className="rc-send"
            disabled={busy || (!draft.trim() && !OPTIONAL_TEXT.includes(step))}
            onClick={submitText}
          >
            <SendIcon fontSize="small" />
          </button>
        </div>
      </>
    );
  };

  /* ── Render ── */

  return (
    <div className="rc-root">
      <div className="rc-header">
        <button className="rc-back" onClick={() => history.push("/")}>
          <ArrowBackIcon fontSize="small" />
        </button>
        <div className="rc-avatar">
          <StorefrontIcon fontSize="small" />
        </div>
        <div className="rc-header-text">
          <div className="rc-header-name">{t.headerName}</div>
          <div className="rc-header-status">
            {typing ? t.typing : t.headerStatus}
          </div>
        </div>
        <button
          className="rc-lang-toggle"
          onClick={() => setLang((l) => (l === "ml" ? "en" : "ml"))}
        >
          {lang === "ml" ? "EN" : "മല"}
        </button>
      </div>

      <div className="rc-progress">
        <div className="rc-progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="rc-thread" ref={threadRef}>
        <div className="rc-thread-inner">
          <div className="rc-day-pill">{t.today}</div>

          {messages.map((m, i) => {
            const prev = messages[i - 1];
            const tail = !prev || prev.from !== m.from;

            if (m.review) {
              return (
                <div className="rc-row" key={m.id}>
                  <div className="rc-review">
                    {reviewRows.map((row) => (
                      <div className="rc-review-row" key={row.key}>
                        <div className="rc-review-label">{row.label}</div>
                        <div className="rc-review-value">
                          {row.images ? (
                            row.images.length ? (
                              <div className="rc-review-thumbs">
                                {row.images.map((src, k) => (
                                  <img src={src} alt="" key={k} />
                                ))}
                              </div>
                            ) : (
                              <span className="empty">—</span>
                            )
                          ) : row.value ? (
                            row.value
                          ) : (
                            <span className="empty">—</span>
                          )}
                        </div>
                        {row.editable === false || reviewLocked ? null : (
                          <button
                            className="rc-review-edit"
                            disabled={busy}
                            onClick={() => editFromReview(row.key)}
                          >
                            {t.edit}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <div className={`rc-row ${m.from}`} key={m.id}>
                <div
                  className={`rc-bubble ${m.from} ${tail ? "tail" : ""} ${
                    m.error ? "error" : ""
                  }`}
                >
                  {m.image && (
                    <div className="rc-photo-wrap">
                      <img className="rc-photo" src={m.image} alt="" />
                      {m.uploading && (
                        <div className="rc-photo-progress">{t.uploading}</div>
                      )}
                    </div>
                  )}
                  {m.text}
                  <span className="rc-meta">
                    {m.ts}
                    {m.from === "me" && (
                      <DoneAllIcon className="rc-tick" style={{ fontSize: 14 }} />
                    )}
                  </span>
                </div>
              </div>
            );
          })}

          {typing && (
            <div className="rc-row">
              <div className="rc-bubble bot tail">
                <div className="rc-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rc-composer">
        <div className="rc-composer-inner">{renderComposer()}</div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="rc-hidden-file"
        onChange={onFilePicked}
      />
    </div>
  );
};

export default RegisterChat;
